import React, { useEffect, useState } from "react";
import { ArrowLeft, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { reApi, errMsg, fmtDateTime, pretty } from "@/lib/residentApi";
import { useHasPerm } from "@/lib/placement";
import { ResidentPicker } from "@/components/nds/windows/resident/ResidentPicker";
import { HowToFix } from "@/components/nds/HowToFix";
import { inputCls, labelCls, GoldButton, Pill, Empty, ErrorLine } from "@/components/nds/windows/resident/ui";
import { OpenResidentButton } from "@/components/nds/OpenResidentButton";

const day = (v) => (v ? new Date(v).toLocaleDateString() : "—");
const SIG_TONE = { signed: "green", pending: "gold", not_required: "dim" };
const openFile = async (id) => window.open(URL.createObjectURL(await reApi.fileBlob(id)), "_blank", "noopener");

const RecordForm = ({ rid, onDone }) => {
  const [f, setF] = useState({ infraction_label: "", tier_label: "", points: 1, incident_at: new Date().toISOString().slice(0, 10), notes: "", signature_status: "pending" });
  const [err, setErr] = useState(null);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const save = async (e) => {
    e.preventDefault(); setErr(null);
    try { await reApi.post("/conduct", { ...f, resident_id: rid, points: Number(f.points) }); toast.success("NCN recorded · $10.00 restitution added to Penalties to verify"); onDone(); }
    catch (x) { setErr(errMsg(x)); }
  };
  return (
    <form onSubmit={save} data-testid="conduct-record-form" className="p-3 border border-white/10 rounded-sm grid grid-cols-2 md:grid-cols-5 gap-2 items-end">
      <label className="md:col-span-2"><span className={labelCls}>Infraction</span><input value={f.infraction_label} onChange={set("infraction_label")} data-testid="conduct-infraction" className={inputCls} /></label>
      <label><span className={labelCls}>Tier</span><input value={f.tier_label} onChange={set("tier_label")} data-testid="conduct-tier" className={inputCls} /></label>
      <label><span className={labelCls}>Points</span><input type="number" min="1" max="25" value={f.points} onChange={set("points")} data-testid="conduct-points" className={inputCls} /></label>
      <label><span className={labelCls}>Date</span><input type="date" value={f.incident_at} onChange={set("incident_at")} data-testid="conduct-date" className={inputCls} /></label>
      <label><span className={labelCls}>Signature</span>
        <select value={f.signature_status} onChange={set("signature_status")} data-testid="conduct-signature" className={inputCls}>
          <option value="pending">Awaiting signature</option><option value="signed">Signed</option><option value="not_required">Not required</option>
        </select></label>
      <label className="col-span-2 md:col-span-3"><span className={labelCls}>Notes</span><input value={f.notes} onChange={set("notes")} data-testid="conduct-notes" className={inputCls} /></label>
      <GoldButton type="submit" disabled={!f.infraction_label.trim()} data-testid="conduct-record-submit">Record NCN · $10.00</GoldButton>
      <div className="col-span-full"><ErrorLine testId="conduct-record-error">{err}</ErrorLine></div>
    </form>
  );
};

const Entry = ({ x, isSuper, onDone }) => {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const voidIt = async () => {
    try { await reApi.patch(`/conduct/${x.id}/void`, { reason }); toast.success("NCN voided"); onDone(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <li data-testid={`conduct-entry-${x.id}`} className={`px-3 py-2 text-sm ${x.voided ? "opacity-50" : ""}`}>
      <button type="button" onClick={() => setOpen((o) => !o)} data-testid={`conduct-entry-toggle-${x.id}`} className="w-full flex items-center gap-2 text-left">
        <span className="font-mono text-nex-gold w-10">−{x.points}</span>
        <span className="flex-1 text-nex-silver">{x.infraction_label}<span className="block text-[11px] text-nex-muted">{day(x.incident_at)} · {x.tier_label || "—"} · score {x.score_before} → {x.score_after}{x.voided ? " · voided" : ""}</span></span>
        <Pill tone={SIG_TONE[x.signature_status] || "dim"} data-testid={`conduct-sig-${x.id}`}>{pretty(x.signature_status)}</Pill>
      </button>
      {open && (
        <div data-testid={`conduct-entry-detail-${x.id}`} className="mt-2 pl-12 space-y-1.5 text-xs text-nex-muted">
          {x.manual_section && <p>Handbook: {x.manual_section}{x.manual_page ? ` · p.${x.manual_page}` : ""}</p>}
          <p>Recorded by {x.recorded_by?.name || x.recorded_by?.email || "—"}{x.signed_at ? ` · signed ${fmtDateTime(x.signed_at)} by ${x.signer_name}` : ""}</p>
          {x.notes && <p className="whitespace-pre-wrap text-nex-silver/80">{x.notes}</p>}
          {x.document_id && <button type="button" onClick={() => openFile(x.document_id)} data-testid={`conduct-writeup-${x.id}`} className="inline-flex items-center gap-1 text-nex-gold"><ExternalLink className="h-3 w-3" /> Open signed write-up</button>}
          {isSuper && !x.voided && (
            <div className="flex gap-2 pt-1">
              <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason to void" data-testid={`conduct-void-reason-${x.id}`} className={`${inputCls} !py-1`} />
              <button type="button" disabled={!reason.trim()} onClick={voidIt} data-testid={`conduct-void-${x.id}`} className="font-mono text-[10px] uppercase text-red-300 disabled:opacity-40">Void</button>
            </div>
          )}
        </div>
      )}
    </li>
  );
};

function ConductView({ resident }) {
  const isSuper = useHasPerm("void_ncn");
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const load = () => reApi.get(`/conduct/residents/${resident.id}`).then(setD).catch((e) => setErr(errMsg(e)));
  useEffect(() => { load(); }, [resident.id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (err) return <ErrorLine testId="conduct-error">{err}</ErrorLine>;
  if (!d) return <p className="text-sm text-nex-muted">Loading…</p>;
  return (
    <div className="space-y-5">
      <div data-testid="conduct-score" className="flex items-baseline gap-3">
        <span className={`font-mono text-4xl ${d.score < 50 ? "text-red-300" : "text-nex-gold"}`}>{d.score}</span>
        <span className="text-sm text-nex-muted">of {d.start_score} · {d.points} points over {d.count} notices</span>
        {d.score < 50 && <HowToFix code="conduct_low" testid="fix-conduct-low" ctx={{ residentId: resident.id }} details={`score ${d.score} of ${d.start_score}, ${d.points} points over ${d.count} notices`} />}
      </div>
      <RecordForm rid={resident.id} onDone={load} />
      <div>
        <div className={labelCls}>NCN notices · newest first</div>
        {d.entries.length ? <ul className="divide-y divide-white/5 border border-white/5 rounded-sm">{d.entries.map((x) => <Entry key={x.id} x={x} isSuper={isSuper} onDone={load} />)}</ul>
          : <Empty testId="conduct-empty">No NCNs.</Empty>}
      </div>
      <div data-testid="conduct-incidents">
        <div className={labelCls}>Incident reports · {d.incidents.length}</div>
        {d.incidents.length ? <ul className="divide-y divide-white/5 border border-white/5 rounded-sm">{d.incidents.map((i) => (
          <li key={i.id} data-testid={`conduct-incident-${i.id}`} className="px-3 py-2 text-sm text-nex-silver">
            <div className="flex items-center gap-2"><span className="flex-1">{i.summary || pretty(i.incident_type)}</span><Pill tone={i.status === "open" ? "gold" : "dim"}>{i.status}</Pill></div>
            <div className="text-[11px] text-nex-muted">{i.incident_date} · {i.severity || "—"} · {i.resident_ids.length > 1 ? `${i.resident_ids.length} residents · ` : ""}{i.description?.slice(0, 160)}</div>
          </li>
        ))}</ul> : <Empty testId="conduct-incidents-empty">No incidents.</Empty>}
      </div>
    </div>
  );
}

export default function ReConductContent({ win }) {
  const [res, setRes] = useState(win?.payload?.resident || null);
  return (
    <div data-testid="re-conduct-content" className="h-full overflow-y-auto p-5 space-y-4">
      {res ? (
        <>
          <button type="button" onClick={() => setRes(null)} data-testid="re-conduct-back" className="inline-flex items-center gap-1 font-mono text-[10px] tracking-[0.22em] uppercase text-nex-gold"><ArrowLeft className="h-3.5 w-3.5" /> Residents</button>
          <div className="flex items-center gap-3"><h3 data-testid="re-conduct-resident" className="text-lg font-semibold text-nex-silver">{res.first_name} {res.last_name}</h3><OpenResidentButton residentId={res.id} name={`${res.first_name} ${res.last_name}`} /></div>
          <ConductView resident={res} />
        </>
      ) : <ResidentPicker prefKey="conduct" onPick={setRes} testid="re-conduct-picker" />}
    </div>
  );
}
